"use client"

import {
  Box,
  HStack,
  SimpleGrid,
  Skeleton,
  type SkeletonProps,
  Stack,
  VStack,
} from "@chakra-ui/react"
import dynamic from "next/dynamic"
import { useEffect, useRef, useState } from "react"

import type { HomeViewModel } from "@/types/getHome"

const SkeletonLine = ({
  h = 3,
  w = "full",
}: {
  h?: SkeletonProps["h"]
  w?: SkeletonProps["w"]
}) => <Skeleton h={h} rounded="full" variant="shine" w={w} />

const HowIWorkSkeleton = () => (
  <Box
    aria-busy="true"
    as="section"
    bgColor="bg.paper"
    borderTop="1px solid {colors.border}"
    data-home-below-fold-skeleton=""
    w="full"
  >
    <VStack
      align="stretch"
      borderLeft="1px solid {colors.border}"
      borderRight="1px solid {colors.border}"
      gap={0}
      maxW="8xl"
      mx="auto"
      w="full"
    >
      <HStack
        borderBottom="1px solid {colors.border}"
        px={{ base: 12 }}
        py={12}
      >
        <SkeletonLine
          h={{ base: 8, md: 10 }}
          w={{ base: "72%", md: "340px" }}
        />
      </HStack>
      <SimpleGrid columns={{ base: 1, md: 2 }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Stack
            borderBottom="1px solid {colors.border}"
            borderLeft={{
              base: "0",
              md: index % 2 === 1 ? "1px solid {colors.border}" : "0",
            }}
            direction={{ base: "column", md: "row" }}
            gap={{ base: 5, md: 6 }}
            key={index}
            px={{ base: 12 }}
            py={12}
          >
            <Skeleton
              flexShrink={0}
              h={16}
              rounded="lg"
              variant="shine"
              w={16}
            />
            <Stack gap={3} w="full">
              <SkeletonLine h={3} w="96px" />
              <SkeletonLine w="92%" />
              <SkeletonLine w="76%" />
            </Stack>
          </Stack>
        ))}
      </SimpleGrid>
    </VStack>
  </Box>
)

const TestimonialsSkeleton = () => (
  <VStack
    aria-busy="true"
    as="section"
    borderBottom="1px solid {colors.border}"
    borderTop="1px solid {colors.border}"
    gap={0}
    position="relative"
    top="-1px"
    w="full"
  >
    <VStack
      gap={8}
      maxW="8xl"
      mx="auto"
      px={{ base: 8, md: 12 }}
      py={12}
      w="full"
    >
      <Skeleton
        h={{ base: 20, md: 28 }}
        rounded="lg"
        variant="shine"
        w="full"
      />
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="full">
        {Array.from({ length: 2 }).map((_, index) => (
          <Stack
            border="1px solid {colors.border}"
            gap={4}
            key={index}
            p={{ base: 6, md: 8 }}
            rounded="sm"
          >
            <HStack gap={4}>
              <Skeleton h={12} rounded="full" variant="shine" w={12} />
              <Stack gap={2} flex="1">
                <SkeletonLine w="46%" />
                <SkeletonLine h={2} w="32%" />
              </Stack>
            </HStack>
            <Stack gap={3}>
              <SkeletonLine w="100%" />
              <SkeletonLine w="88%" />
              <SkeletonLine w="64%" />
            </Stack>
          </Stack>
        ))}
      </SimpleGrid>
    </VStack>
  </VStack>
)

const LetsTalkSkeleton = () => (
  <Box aria-busy="true" as="section" bg="bg.paper" overflow="hidden" w="full">
    <HStack
      borderLeft="1px solid {colors.border}"
      borderRight="1px solid {colors.border}"
      maxW="8xl"
      mx="auto"
      px={{ base: 12 }}
      py={12}
      w="full"
    >
      <Stack gap={4} maxW="720px" w="full">
        <SkeletonLine
          h={{ base: 7, md: 10 }}
          w={{ base: "88%", md: "520px" }}
        />
        <SkeletonLine w="100%" />
        <SkeletonLine w="72%" />
        <Skeleton h={10} rounded="full" variant="shine" w="148px" />
      </Stack>
    </HStack>
  </Box>
)

const HomeBelowFoldSkeleton = () => (
  <>
    <HowIWorkSkeleton />
    <TestimonialsSkeleton />
    <LetsTalkSkeleton />
  </>
)

// Below-the-fold sections: keep them out of the initial load path. Testimonials
// pulls in Swiper, so we wait until the skeleton is close to the viewport.
const HowIWork = dynamic(() => import("@/sections/HowIWork"), {
  loading: () => <HowIWorkSkeleton />,
  ssr: false,
})
const Testimonials = dynamic(() => import("@/sections/Testimonials"), {
  loading: () => <TestimonialsSkeleton />,
  ssr: false,
})
const LetsTalk2 = dynamic(() => import("@/sections/LetsTalk2"), {
  loading: () => <LetsTalkSkeleton />,
  ssr: false,
})
const Footer = dynamic(() => import("@/sections/Footer"), {
  loading: () => null,
  ssr: false,
})

interface HomeBelowFoldProps {
  query: HomeViewModel
}

/**
 * Renders below-the-fold sections when they are near the viewport, on scroll,
 * or after a long fallback delay. Keeps the LCP/TTI path lean.
 */
const HomeBelowFold = ({ query }: HomeBelowFoldProps) => {
  const [shouldRender, setShouldRender] = useState(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (shouldRender) return

    const trigger = () => setShouldRender(true)
    const target = sentinelRef.current
    let observer: IntersectionObserver | undefined

    if (target && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return
          trigger()
          observer?.disconnect()
        },
        { rootMargin: "900px 0px", threshold: 0 }
      )
      observer.observe(target)
    }

    const onScroll = () => {
      trigger()
    }

    const timeoutHandle = setTimeout(trigger, 12000)

    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("touchstart", onScroll, { passive: true })
    window.addEventListener("wheel", onScroll, { passive: true })

    return () => {
      observer?.disconnect()
      clearTimeout(timeoutHandle)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("touchstart", onScroll)
      window.removeEventListener("wheel", onScroll)
    }
  }, [shouldRender])

  if (!shouldRender) {
    return (
      <Box ref={sentinelRef} w="full">
        <HomeBelowFoldSkeleton />
      </Box>
    )
  }

  return (
    <>
      <HowIWork heading={query.methodHeading} items={query.methodItems} />
      <Testimonials data={query.testimonials} />
      <LetsTalk2
        cta={query.finalCta}
        description={query.finalCtaDescription}
        openToWork={query.hero.badgeText}
        title={query.finalCtaTitle}
      />
      <Footer />
    </>
  )
}

export default HomeBelowFold
